import React, { useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { ShieldCheck, FlaskConical, CheckCircle2, AlertTriangle } from "lucide-react";
import { formatAuthError } from "../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = useMemo(() => params.get("token") || "", [params]);

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const missingToken = !token;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (password !== confirm) {
      setError("Passwords do not match.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, password });
      setDone(true);
      setTimeout(() => navigate("/login", { replace: true }), 2500);
    } catch (err) {
      setError(formatAuthError(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main
      data-testid="reset-password-page"
      className="min-h-[calc(100vh-7rem)] bg-cream-200/40 py-14 md:py-20"
    >
      <div className="container-x max-w-md">
        <div className="mb-8 text-center">
          <div className="mx-auto w-12 h-12 rounded-full bg-sage-100 flex items-center justify-center mb-5">
            <FlaskConical className="w-5 h-5 text-sage-600" strokeWidth={1.8} />
          </div>
          <p className="overline mb-2">Set a new password</p>
          <h1 className="font-display text-4xl md:text-5xl font-bold text-charcoal-600 leading-tight">
            Choose a new password
          </h1>
          <p className="mt-3 text-sm text-charcoal-400">
            Pick something you'll remember. Minimum 8 characters.
          </p>
        </div>

        {missingToken ? (
          <div
            data-testid="reset-password-missing-token"
            className="surface-card p-8 md:p-10 text-center space-y-5"
          >
            <div className="mx-auto w-14 h-14 rounded-full bg-red-50 flex items-center justify-center">
              <AlertTriangle className="w-6 h-6 text-red-600" strokeWidth={1.8} />
            </div>
            <h2 className="font-display text-2xl font-bold text-charcoal-600">
              Missing reset token
            </h2>
            <p className="text-sm text-charcoal-400">
              This page needs a reset token — please use the link from your reset email.
            </p>
            <Link
              to="/forgot-password"
              data-testid="reset-password-request-new"
              className="btn-primary inline-flex"
            >
              Request a new reset link
            </Link>
          </div>
        ) : done ? (
          <div
            data-testid="reset-password-done"
            className="surface-card p-8 md:p-10 text-center space-y-5"
          >
            <div className="mx-auto w-14 h-14 rounded-full bg-sage-100 flex items-center justify-center">
              <CheckCircle2 className="w-6 h-6 text-sage-700" strokeWidth={1.8} />
            </div>
            <h2 className="font-display text-2xl font-bold text-charcoal-600">
              Password updated
            </h2>
            <p className="text-sm text-charcoal-400">
              Your password has been changed. Redirecting you to sign in…
            </p>
            <Link
              to="/login"
              data-testid="reset-password-go-to-login"
              className="btn-primary inline-flex"
            >
              Sign in now
            </Link>
          </div>
        ) : (
          <form
            onSubmit={handleSubmit}
            className="surface-card p-8 md:p-10 space-y-5"
            data-testid="reset-password-form"
          >
            <label htmlFor="new-password" className="block">
              <span className="block text-xs font-medium uppercase tracking-[0.18em] text-charcoal-400 mb-2">
                New password
              </span>
              <input
                id="new-password"
                data-testid="reset-password-new"
                type="password"
                autoComplete="new-password"
                required
                minLength={8}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="input"
              />
            </label>

            <label htmlFor="confirm-password" className="block">
              <span className="block text-xs font-medium uppercase tracking-[0.18em] text-charcoal-400 mb-2">
                Confirm new password
              </span>
              <input
                id="confirm-password"
                data-testid="reset-password-confirm"
                type="password"
                autoComplete="new-password"
                required
                minLength={8}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className="input"
              />
            </label>

            {error && (
              <p
                data-testid="reset-password-error"
                className="text-sm text-red-700 bg-red-50 border border-red-100 rounded-xl px-4 py-3"
              >
                {error}
              </p>
            )}

            <button
              type="submit"
              data-testid="reset-password-submit"
              disabled={submitting}
              className="btn-primary w-full disabled:opacity-60 disabled:cursor-not-allowed"
            >
              <ShieldCheck className="w-4 h-4 mr-2" strokeWidth={2} />
              {submitting ? "Updating…" : "Update password"}
            </button>
          </form>
        )}
      </div>
    </main>
  );
}
